/**
 * v3.18-1300 COMM-TIMELINE-001 — Unified Communication Timeline.
 *
 * Single chronological history covering every channel bound to a
 * client. Filter chips let operators focus on one signal (calls,
 * SMS, email, voicemail, notes, tasks, appointments, documents,
 * payments, generic activities).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  PhoneCall, MessageSquare, Mail, Voicemail, StickyNote, ClipboardList,
  CalendarClock, FileText, DollarSign, Activity, RefreshCcw, X,
  User, Building2, Paperclip, ExternalLink, Pin,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { getAttentionStyle } from '../lib/noteAttention';

const CHANNEL_META = {
  call:        { icon: PhoneCall,      label: 'Calls',        color: 'text-emerald-600', bg: 'bg-emerald-50' },
  sms:         { icon: MessageSquare,  label: 'SMS',          color: 'text-blue-600',    bg: 'bg-blue-50' },
  email:       { icon: Mail,           label: 'Email',        color: 'text-indigo-600',  bg: 'bg-indigo-50' },
  voicemail:   { icon: Voicemail,      label: 'Voicemail',    color: 'text-purple-600',  bg: 'bg-purple-50' },
  note:        { icon: StickyNote,     label: 'Notes',        color: 'text-amber-700',   bg: 'bg-amber-50' },
  ghost_task:  { icon: ClipboardList,  label: 'Ghost tasks',  color: 'text-slate-700',   bg: 'bg-slate-50' },
  appointment: { icon: CalendarClock,  label: 'Appointments', color: 'text-rose-700',    bg: 'bg-rose-50' },
  document:    { icon: FileText,       label: 'Documents',    color: 'text-cyan-700',    bg: 'bg-cyan-50' },
  payment:     { icon: DollarSign,     label: 'Payments',     color: 'text-emerald-800', bg: 'bg-emerald-50' },
};

const activityMeta = () => ({
  icon: Activity, label: 'Activity', color: 'text-slate-600', bg: 'bg-white',
});

const eventMeta = (e) => {
  const t = e.type || '';
  if (CHANNEL_META[t]) return CHANNEL_META[t];
  if (t.startsWith('activity:')) return activityMeta();
  return activityMeta();
};

const fmtDT = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch (_e) { return iso; }
};

const UnifiedTimeline = ({ clientId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(new Set(Object.keys(CHANNEL_META)));
  // v3.18-1301 event-detail dialog state.
  const [selectedEvent, setSelectedEvent] = useState(null);

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/unified-timeline`, {
        params: { limit: 500 },
      });
      setEvents(data?.events || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load timeline');
      setEvents([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [clientId]);

  const toggle = (key) => setActive((s) => {
    const next = new Set(s);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const selectAll = () => setActive(new Set(Object.keys(CHANNEL_META)));
  const selectNone = () => setActive(new Set());

  const filtered = useMemo(() => {
    return events.filter((e) => {
      const t = (e.type || '').startsWith('activity:') ? 'activity' : e.type;
      if (t === 'activity' && active.size === 0) return false;
      if (t === 'activity') return true;
      return active.has(t);
    });
  }, [events, active]);

  const counts = useMemo(() => {
    const c = {};
    for (const e of events) {
      const t = (e.type || '').startsWith('activity:') ? 'activity' : e.type;
      c[t] = (c[t] || 0) + 1;
    }
    return c;
  }, [events]);

  return (
    <div data-testid="unified-timeline">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-800">Communication Timeline</h3>
          <Badge variant="outline" className="text-[10px]">{events.length}</Badge>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={selectAll} className="text-[10px] text-slate-500 hover:underline">All</button>
          <span className="text-slate-300">/</span>
          <button onClick={selectNone} className="text-[10px] text-slate-500 hover:underline">None</button>
          <button
            onClick={load}
            className="h-6 w-6 rounded flex items-center justify-center text-slate-500 hover:bg-slate-100"
            title="Refresh"
            data-testid="unified-timeline-refresh"
          >
            <RefreshCcw className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-1 mb-3" data-testid="unified-timeline-filters">
        {Object.entries(CHANNEL_META).map(([key, m]) => {
          const on = active.has(key);
          const Icon = m.icon;
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              data-testid={`unified-timeline-filter-${key}`}
              className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition
                ${on ? `${m.bg} ${m.color} border-transparent`
                     : 'bg-white text-slate-400 border-slate-200 hover:border-slate-400'}`}
            >
              <Icon className="h-3 w-3" />
              {m.label}
              <span className={`ml-1 rounded-full px-1 text-[10px] ${on ? 'bg-white/60' : 'bg-slate-100'}`}>
                {counts[key] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-xs text-slate-400 py-4">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-xs text-slate-400 py-4" data-testid="unified-timeline-empty">
          No events for the selected filters.
        </div>
      ) : (
        <div className="space-y-1 relative pl-3 border-l border-slate-200">
          {filtered.map((e) => {
            const meta = eventMeta(e);
            const Icon = meta.icon;
            // v3.21-1667 · Only apply attention treatment to notes.
            const isNoteEvt = e.type === 'note' || e.type === 'activity:note_added'
                              || e.type === 'activity:note' || e.type === 'activity:internal_note_added';
            const att = isNoteEvt ? getAttentionStyle(e.attention_color) : null;
            const bgCls = att ? att.tint : meta.bg;
            const accentCls = att ? att.accent : '';
            return (
              <div
                key={`${e.source_collection || 'x'}-${e.id || Math.random()}`}
                onClick={() => setSelectedEvent(e)}
                onKeyDown={(k) => { if (k.key === 'Enter') setSelectedEvent(e); }}
                role="button"
                tabIndex={0}
                className={`relative rounded-md border border-slate-200 ${bgCls} ${accentCls} p-2 cursor-pointer hover:shadow-sm transition`}
                data-testid={`unified-timeline-event-${e.id || e.type}`}
                data-attention-color={att ? att.color : 'neutral'}
              >
                <div className="absolute -left-[15px] top-3 h-2.5 w-2.5 rounded-full bg-white border-2 border-slate-300" />
                <div className="flex items-start gap-2">
                  <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${meta.color}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="text-[12px] font-semibold text-slate-800 truncate">
                        {e.title}
                      </div>
                      {e.direction && (
                        <span className={`text-[9px] uppercase px-1 rounded ${
                          e.direction === 'inbound' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-700'
                        }`}>
                          {e.direction}
                        </span>
                      )}
                      {isNoteEvt && e.is_pinned && (
                        <span className="text-[9px] uppercase px-1 rounded bg-cyan-100 text-cyan-700 inline-flex items-center gap-0.5">
                          <Pin className="h-2.5 w-2.5" /> Pinned
                        </span>
                      )}
                      {att && (
                        <span
                          className={`text-[9px] uppercase px-1.5 py-[1px] rounded border ${att.badge}`}
                          data-testid={`unified-timeline-attention-${e.id}`}
                        >
                          {att.label}
                        </span>
                      )}
                    </div>
                    {e.body && (
                      <div className="text-[11px] text-slate-600 mt-0.5 line-clamp-3 whitespace-pre-wrap">
                        {e.body}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{fmtDT(e.timestamp)}{e.actor_name ? ` · ${e.actor_name}` : ''}</span>
                      {/* v3.21-1733 · Move to Activity (non-destructive
                          promotion).  Only shown for eligible source
                          collections; disabled/labelled "Already in
                          Activity" when the row is already an activity
                          or already promoted. */}
                      {(() => {
                        const alreadyActivity = String(e.type || '').startsWith('activity:')
                                              || e.source_collection === 'activities';
                        const alreadyPromoted = !!e.activity_item_id;
                        const promotable = ['communications','client_emails','email_sent_log',
                                            'call_notes','note_drafts','sms_logs','call_logs']
                                             .includes(e.source_collection || '');
                        if (alreadyActivity) {
                          return <span className="text-[9px] uppercase text-emerald-700 bg-emerald-100 px-1.5 py-[1px] rounded">In Activity</span>;
                        }
                        if (alreadyPromoted) {
                          return <span className="text-[9px] uppercase text-emerald-700 bg-emerald-100 px-1.5 py-[1px] rounded">Already in Activity</span>;
                        }
                        if (!promotable) return null;
                        return (
                          <button
                            type="button"
                            data-testid={`timeline-move-to-activity-${e.id}`}
                            onClick={async (ev) => {
                              ev.stopPropagation();
                              try {
                                const r = await api.post('/activity-bridge/promote-to-activity', {
                                  source_collection: e.source_collection,
                                  source_id:         e.id,
                                  client_id:         clientId,
                                });
                                if (r.data?.already_in_activity) {
                                  toast.success('Already in Activity');
                                } else {
                                  toast.success('Moved to Activity');
                                }
                                await load();
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || 'Could not move to Activity');
                              }
                            }}
                            className="text-[10px] px-1.5 py-[1px] rounded bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300"
                          >
                            Move to Activity
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <TimelineEventDetailDialog
        clientId={clientId}
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    </div>
  );
};

import { Voicemail as VoicemailIcon } from 'lucide-react';
import { VoicemailAudioPlayer, playbackErrorText } from './VoicemailAudioPlayer';
// v3.21-1634 · Block 3 · Client-Detail Play Recording integration.
// The Play button below mints the same signed short-lived Hub media
// URL used by the /phone-system/recordings page — no duplicate call
// record is created and the browser never sees a Twilio credential
// or the raw Twilio media URL.
const InlineRecordingPlayer = ({ callLogId }) => {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/telephony/recordings/${callLogId}/media-token`, { intent: 'play' });
      const res = await api.get(data.media_url.replace(/^\/api/, ''), { responseType: 'blob' });
      setMediaUrl(URL.createObjectURL(res.data));
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403) toast.error('Call recordings are internal-only.');
      else toast.error(await playbackErrorText(e));
    } finally { setLoading(false); }
  };
  useEffect(() => () => { if (mediaUrl) URL.revokeObjectURL(mediaUrl); }, [mediaUrl]);
  if (mediaUrl) {
    return <audio src={mediaUrl} controls preload="auto" autoPlay className="h-9 w-full"
                  data-testid={`timeline-audio-${callLogId}`} />;
  }
  return (
    <button
      type="button" onClick={load} disabled={loading}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded bg-white hover:bg-slate-50 text-slate-700"
      data-testid={`timeline-play-recording-${callLogId}`}
    >
      <VoicemailIcon className="w-3.5 h-3.5" />
      {loading ? 'Loading…' : 'Play Recording'}
    </button>
  );
};

// ── v3.18-1301 Event detail dialog ────────────────────────────────
const TimelineEventDetailDialog = ({ clientId, event, onClose }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!event || !clientId) return;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    (async () => {
      try {
        const { data } = await api.get(
          `/clients/${clientId}/timeline-event/${encodeURIComponent(event.type)}/${encodeURIComponent(event.id)}`,
        );
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || 'Failed to load event detail');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [event, clientId]);

  if (!event) return null;
  const meta = eventMeta(event);
  const Icon = meta.icon;
  const doc = detail?.document || {};
  const rel = detail?.related || {};
  const attachments = doc.attachments || doc.decoded_attachments || [];

  const goToRelatedClient = () => {
    if (rel?.client?.id) navigate(`/clients/${rel.client.id}`);
    onClose();
  };

  return (
    <Dialog open={!!event} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="timeline-event-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${meta.color}`} />
            <span className="flex-1">{event.title || event.type}</span>
            <button
              type="button" onClick={onClose}
              className="h-7 w-7 rounded flex items-center justify-center hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
            </button>
          </DialogTitle>
          <DialogDescription>
            {event.timestamp && new Date(event.timestamp).toLocaleString()}
            {event.actor_name ? ` · ${event.actor_name}` : ''}
            {event.direction ? ` · ${event.direction}` : ''}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="text-xs text-slate-400 py-4">Loading detail…</div>}

        {!loading && (
          <div className="space-y-4">
            {/* Body / original content */}
            <section>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Content
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-sm whitespace-pre-wrap text-slate-800">
                {doc.body_text || doc.body || doc.description || doc.notes || event.body || '(no content captured)'}
              </div>
            </section>

            {/* v3.21-1634 · Block 3 · Play Recording for call events with associated
                recording_sid/recording_url.  Backend enforces internal-only. */}
            {(event.type === 'call' || event.channel === 'call'
              || doc.recording_sid || doc.recording_url) && (doc.recording_sid || doc.recording_url) && (
              <section>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
                  <VoicemailIcon className="h-3 w-3" /> Call Recording
                </div>
                <InlineRecordingPlayer callLogId={doc.id || event.id} />
              </section>
            )}
            {event.type === 'voicemail' && doc.has_recording && (
              <section>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
                  <VoicemailIcon className="h-3 w-3" /> Voicemail
                </div>
                <VoicemailAudioPlayer voicemailId={doc.id || event.id} initialDuration={doc.duration} />
              </section>
            )}

            {/* Attachments */}
            {attachments.length > 0 && (
              <section>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
                  <Paperclip className="h-3 w-3" /> Attachments ({attachments.length})
                </div>
                <ul className="text-xs space-y-0.5">
                  {attachments.map((a, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <FileText className="h-3 w-3 text-slate-400" />
                      {a.filename || a.name || 'attachment'} — {a.size || a.bytes || ''}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Related entities */}
            <section className="grid grid-cols-2 gap-2 text-xs">
              {rel.employee && (
                <div className="border rounded p-2 bg-white">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <User className="h-3 w-3" /> Employee
                  </div>
                  <div className="font-medium text-slate-800">
                    {rel.employee.full_name || `${rel.employee.first_name || ''} ${rel.employee.last_name || ''}`.trim() || rel.employee.email}
                  </div>
                  {rel.employee.role && <div className="text-slate-500">{rel.employee.role}</div>}
                </div>
              )}
              {rel.client && (
                <div className="border rounded p-2 bg-white">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <User className="h-3 w-3" /> Client
                  </div>
                  <div className="font-medium text-slate-800">{rel.client.full_name}</div>
                  <button
                    type="button" onClick={goToRelatedClient}
                    className="text-[10px] text-indigo-600 hover:underline mt-0.5 inline-flex items-center gap-0.5"
                  >
                    Open full profile <ExternalLink className="h-2.5 w-2.5" />
                  </button>
                </div>
              )}
              {rel.company && (
                <div className="border rounded p-2 bg-white">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <Building2 className="h-3 w-3" /> Company
                  </div>
                  <div className="font-medium text-slate-800">
                    {rel.company.name || rel.company.company_name}
                  </div>
                </div>
              )}
              {rel.ghost_task && (
                <div className="border rounded p-2 bg-white">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <ClipboardList className="h-3 w-3" /> Ghost task
                  </div>
                  <div className="font-medium text-slate-800">{rel.ghost_task.title}</div>
                  <div className="text-slate-500 text-[10px]">{rel.ghost_task.status}</div>
                </div>
              )}
              {rel.appointment && (
                <div className="border rounded p-2 bg-white">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <CalendarClock className="h-3 w-3" /> Appointment
                  </div>
                  <div className="font-medium text-slate-800">{rel.appointment.title || 'Appointment'}</div>
                  <div className="text-slate-500 text-[10px]">
                    {rel.appointment.scheduled_at || rel.appointment.start_time}
                  </div>
                </div>
              )}
              {rel.payment && (
                <div className="border rounded p-2 bg-white">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <DollarSign className="h-3 w-3" /> Payment
                  </div>
                  <div className="font-medium text-slate-800">
                    ${(rel.payment.amount || 0).toLocaleString()} — {rel.payment.status}
                  </div>
                </div>
              )}
            </section>

            {/* Client documents */}
            {rel.documents?.length > 0 && (
              <section>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                  Recent client documents ({rel.documents.length})
                </div>
                <ul className="text-xs space-y-0.5 max-h-40 overflow-y-auto">
                  {rel.documents.slice(0, 10).map((d, i) => (
                    <li key={d.id || i} className="flex items-center gap-2 text-slate-700">
                      <FileText className="h-3 w-3 text-slate-400" />
                      {d.filename || d.name} · {d.category || ''}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Raw meta collapsible */}
            <details className="text-[10px] text-slate-400">
              <summary className="cursor-pointer text-slate-500 uppercase tracking-wider font-semibold">
                Raw data
              </summary>
              <pre className="mt-1 bg-slate-900 text-slate-100 rounded p-2 overflow-x-auto text-[10px]">
                {JSON.stringify(doc, null, 2).slice(0, 4000)}
              </pre>
            </details>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedTimeline;
